import { FC, useState, useEffect, useRef } from 'react';
import {useIntl} from 'react-intl'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

import clipboardCopy from 'clipboard-copy';
import toastr from 'toastr';

import { ChatMessageModel, ChatMessageFileModel, ChatMessageRoleType } from '../../../models/chat';
import { decodeUnicode, removeThinkingPart } from '../../../utils';

import { chatMessageAPI } from '../../../api'

import { useAuth } from '../../../context/auth'
import {useLang} from '../../../../_metronic/i18n/Metronici18n'

import { ChatMessageGenerationType } from '../../../models';

import { MenuComponent } from '../../../../_metronic/assets/ts/components'

import { removeIslamicTAGs } from '../../../components/MarkdownWithLinks'
import { Chat } from './Chat';

interface Props {
  isSystemMessage?: boolean
  chatMessage: ChatMessageModel
  withTyping?: boolean
  isTyping?: boolean
  isLast?: boolean
  emptyGeneratedFile: (type: string) => void
  setGeneratedFile: (chatMessageFile: ChatMessageFileModel, type: string) => void
  generatedAudioUrl: string | undefined
  setAudioAutoPlay: (autoPlay: boolean) => void
  setSearchedImages: (images: [] | undefined) => void
  setDiacritizedMessage: (message: string | undefined) => void
  setIsTyping?: (isTyping:boolean) => void
  handleRegenerate?: () => void
  setShowAttributionModal?: (show: boolean) => void
  messageReaction?: string | undefined
  audioRef: React.RefObject<HTMLAudioElement>
  translatedMessage: string | undefined
  setTranslatedMessage: (message: string | undefined) => void
  handleReaction: (reaction: string, type: string) => void
  downloadFile: (url: string, type: string) => void
}


const ChatMessageOptions: FC<Props> = ({ isSystemMessage = false, chatMessage, withTyping, isTyping, handleRegenerate, isLast, handleReaction, setAudioAutoPlay, setSearchedImages, setDiacritizedMessage, setShowAttributionModal, messageReaction, audioRef, translatedMessage, setTranslatedMessage, emptyGeneratedFile, setGeneratedFile, generatedAudioUrl, downloadFile}) => {
  
  const intl = useIntl()

  const selectedLang = useLang()

  const {currentUser} = useAuth()

  const currentSettings = currentUser?.settings

  const isText = [ChatMessageGenerationType.TEXT, ChatMessageGenerationType.IMAGE_UNDERSTANDING, ChatMessageGenerationType.VIDEO_UNDERSTANDING, ChatMessageGenerationType.TRANSCRIPTION].includes(chatMessage.generation_type)

  const isEligibleForTranslationAndTTS = !/<quran_start>|<quran_end>/i.test(chatMessage.message)

  const [isAttributable, setIsAttributable] = useState<boolean | null>(null)
  const attributableIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const [generatedAudioType, setGeneratedAudioType] = useState<string | undefined>()
  const [isSpeechPlaying, setIsSpeechPlaying] = useState(false)
  const [isMusicPlaying, setIsMusicPlaying] = useState(false)

  const [translatingMessage, setTranslatingMessage] = useState<boolean>(false)
  const [generatingImage, setGeneratingImage] = useState<boolean>(false)
  const [searchingImages, setSearchingImages] = useState<boolean>(false)
  const [generatingAudio, setGeneratingAudio] = useState<boolean>(false)
  const [generatingSpeech, setGeneratingSpeech] = useState<boolean>(false)
  const [generatingMusic, setGeneratingMusic] = useState<boolean>(false)
  const [diacritizingMessage, setDiacritizingMessage] = useState<boolean>(false)


  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  useEffect(() => {
    if( chatMessage.role == ChatMessageRoleType.ASSISTANT ){
      setIsAttributable(chatMessage.is_attributable != undefined && chatMessage.is_attributable)
    }
  }, [chatMessage])


  useEffect(() => {
    if( chatMessage.role == ChatMessageRoleType.ASSISTANT && isText ){
      const checkAttributable = () => {
        if( chatMessage.id ){
          chatMessageAPI.isAttributable(chatMessage.chat_id, chatMessage.id).then((response) => {
            if( response['data']['data']['is_attributable'] != null ){
              setIsAttributable(response['data']['data']['is_attributable'])
              if( attributableIntervalRef.current ){
                clearTimeout(attributableIntervalRef.current);
              }
            }else{
              attributableIntervalRef.current = setTimeout(checkAttributable, 1000)
            }
          })
        }
      };

      if( chatMessage.is_attributable == undefined && currentSettings?.content_setting?.attribution ){
        attributableIntervalRef.current = setTimeout(checkAttributable, 1000)
      }
    }
    return () => {
      if (attributableIntervalRef.current) {
        clearTimeout(attributableIntervalRef.current)
      }
    };
  }, [chatMessage])


  const handleCopy = () => {
    let copyingMessage = decodeUnicode(removeThinkingPart(chatMessage.message))
    copyingMessage = removeIslamicTAGs(copyingMessage)
    if( chatMessage.references && chatMessage.references.length > 0 ){
      copyingMessage += '\n\n'
      +
      chatMessage.references.map((reference: any) => `[${reference['number']}] ${reference['source']}`).join('\n')
    }
	  clipboardCopy(copyingMessage);
	  toastr.info(intl.formatMessage({id: `chat.message.copied`}))
  };

  const stopAudio = () => {
    if ( audioRef.current ){
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    generatedAudioType && emptyGeneratedFile(generatedAudioType)
    setIsMusicPlaying(false)
    setIsSpeechPlaying(false)
    setAudioAutoPlay(false)
  }

  const generateAudio = async (type: string) => {
    if( chatMessage.id ){
      setGeneratedAudioType(type)
      stopAudio();
      setGeneratingAudio(true)

      if( type == ChatMessageGenerationType.MUSIC ){
        setGeneratingMusic(true)
      }else{
        setGeneratingSpeech(true)
      }

      try{
        const response = await chatMessageAPI.generateFile(chatMessage.chat_id, chatMessage.id, type, isSystemMessage)
        if (response.status === 200) {
          setGeneratingAudio(false)
          if( type === ChatMessageGenerationType.MUSIC ){
            setIsMusicPlaying(true)
            setGeneratingMusic(false)
          }else{
            setIsSpeechPlaying(true)
            setGeneratingSpeech(false)
          }
          setAudioAutoPlay(true)
          setGeneratedFile(response.data.data, type);

          if( audioRef.current ){
            audioRef.current.onended = function(){
              if( type === ChatMessageGenerationType.MUSIC ){
                setIsMusicPlaying(false)
              }else{
                setIsSpeechPlaying(false)
              }
            };
          }
        }else{
          if( type === ChatMessageGenerationType.MUSIC ){
            setIsMusicPlaying(false)
            setGeneratingMusic(false)
          }else{
            setIsSpeechPlaying(false)
            setGeneratingSpeech(false)
          }
        }
      }catch(e){
        if( type === ChatMessageGenerationType.MUSIC ){
          setIsMusicPlaying(false)
          setGeneratingMusic(false)
        }else{
          setIsSpeechPlaying(false)
          setGeneratingSpeech(false)
        }
        setGeneratingAudio(false)
        console.error(e)
      }
    }
  } 

  const generateImage = async () => {
    if( chatMessage.id ){
      setGeneratingImage(true)
      emptyGeneratedFile(ChatMessageGenerationType.IMAGE)
      try{
        const response = await chatMessageAPI.generateFile(chatMessage.chat_id, chatMessage.id, ChatMessageGenerationType.IMAGE, isSystemMessage)
        if (response.status === 200) {
          setGeneratedFile(response.data.data, ChatMessageGenerationType.IMAGE);
        }
      }finally{
        setGeneratingImage(false)
      }
    }
  }
  
  const searchImages = async () => {
    if( chatMessage.id ){
      setSearchingImages(true)
      try{
        const response = await chatMessageAPI.searchImages(chatMessage.chat_id, chatMessage.id, isSystemMessage)
        if (response.status === 200) {
          setSearchedImages(response.data.data)
        }
      }finally{
        setSearchingImages(false)
      }
    }
  }

  const translate = async () => {
    if( chatMessage.id ){
      setTranslatingMessage(true)
      try{
        const response = await chatMessageAPI.translate(chatMessage.chat_id, chatMessage.id, isSystemMessage)
        if (response.status === 200) {
          setTranslatedMessage(response.data.data.translated_message)
        }else{
          toastr.error(intl.formatMessage({id: 'chat.message.translate_failed'}))
        }
      }finally{
        setTranslatingMessage(false)
      }
    }
  }

  const diacritize = async () => {
    if( chatMessage.id ){
      setDiacritizingMessage(true)
      try{
        const response = await chatMessageAPI.diacritize(chatMessage.chat_id, chatMessage.id, isSystemMessage)
        if (response.status === 200) {
          setDiacritizedMessage(response.data.data.message)
        }else{
          toastr.error(intl.formatMessage({id: 'chat.message.diacritize_failed'}))
        }
      }finally{
        setDiacritizingMessage(false)
      }
    }
  }

  return <div
    className={`chat-message-options ${
      (chatMessage.id == undefined &&
        chatMessage.role == ChatMessageRoleType.ASSISTANT) ||
      chatMessage.is_filtered ||
      (withTyping && isTyping)
        ? "d-none"
        : ""
    }`}
  >
    {
    (
      ( chatMessage.role == ChatMessageRoleType.ASSISTANT && isText ) || 
      ( chatMessage.message && chatMessage.role === ChatMessageRoleType.USER )
    ) && (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            {intl.formatMessage({ id: "chat.message.copy" })}
          </Tooltip>
        }
      >
        <span
          className="cursor-pointer me-4 text-hover-dark py-1"
          onClick={handleCopy}
        >
          <i className="fa-solid fa-copy fs-5"></i>
        </span>
      </OverlayTrigger>
    )}
    {
      chatMessage.role == ChatMessageRoleType.ASSISTANT &&
      chatMessage.generation_type === ChatMessageGenerationType.TEXT &&
      isAttributable &&
      currentSettings?.content_setting?.attribution &&
      setShowAttributionModal && (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {intl.formatMessage({
                id: "chat.message.attribute.title",
              })}
            </Tooltip>
          }
        >
          <span
            className="cursor-pointer me-4 text-hover-dark py-1"
            onClick={() => setShowAttributionModal(true)}
          >
            <i className="fa-solid fa-book-open fs-5"></i>
          </span>
        </OverlayTrigger>
      )
    }
    {
      chatMessage.role == ChatMessageRoleType.ASSISTANT &&
      [ChatMessageGenerationType.SPEECH, ChatMessageGenerationType.MUSIC].includes(chatMessage.generation_type) &&
      generatedAudioUrl &&
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            {intl.formatMessage({ id: "general.download" })}
          </Tooltip>
        }
      >
        <span
          className={`cursor-pointer text-hover-dark me-4 py-1`}
          onClick={() => downloadFile(generatedAudioUrl, chatMessage.generation_type)}
        >
          <i className="fa-solid fa-download fs-5"></i>
        </span>
      </OverlayTrigger>
    }
    {
      chatMessage.role == ChatMessageRoleType.ASSISTANT &&
      chatMessage.generation_type !== ChatMessageGenerationType.IMAGE &&
      currentSettings?.response_setting?.reaction && (
        <>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {intl.formatMessage({
                  id: "chat.message.good_response",
                })}
              </Tooltip>
            }
          >
            <span
              className={`cursor-pointer text-hover-dark me-4 py-1`}
              onClick={() => handleReaction("like", chatMessage.generation_type)}
            >
              <i className={`fa-solid fa-thumbs-up ${messageReaction === 'like' && 'text-primary'} fs-5`}></i>
            </span>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {intl.formatMessage({
                  id: "chat.message.bad_response",
                })}
              </Tooltip>
            }
          >
            <span
              className={`cursor-pointer text-hover-dark me-4 py-1`}
              onClick={() => handleReaction("dislike", chatMessage.generation_type)}
            >
              <i className={`fa-solid fa-thumbs-down ${messageReaction === 'dislike' && 'text-danger'} fs-5`}></i>
            </span>
          </OverlayTrigger>
        </>
      )
    }

    {
      (
        (
          isText &&
          isEligibleForTranslationAndTTS &&
          chatMessage.role == ChatMessageRoleType.ASSISTANT &&
          currentSettings?.response_setting?.text_to_speech
        ) ||
        (
          chatMessage.id &&
          chatMessage.role == ChatMessageRoleType.USER &&
          chatMessage.generation_type != ChatMessageGenerationType.SPEECH &&
          chatMessage.generation_type != ChatMessageGenerationType.TRANSCRIPTION &&
          currentSettings?.prompt_setting?.text_to_speech)) &&
        (
        !isSpeechPlaying 
        ?
        (
          <OverlayTrigger
            placement="top"
            trigger={["hover", "focus"]}
            overlay={
              <Tooltip>
                {intl.formatMessage({
                  id: `chat.message.${
                    generatingSpeech ? "listen_downloading" : "listen"
                  }`,
                })}
              </Tooltip>
            }
          >
            <span
              className={`${
                !generatingAudio
                  ? "cursor-pointer text-hover-dark"
                  : generatingSpeech && "blinking"
              } me-4 py-1`}
              onClick={() => {
                !generatingAudio && generateAudio("speech");
              }}
            >
              <i className="fa-solid fa-volume-high fs-5"></i>
            </span>
          </OverlayTrigger>
        ) 
        : 
        (
          <OverlayTrigger
            placement="top"
            trigger={["hover", "focus"]}
            overlay={
              <Tooltip>
                {intl.formatMessage({ id: `chat.message.listening` })}
              </Tooltip>
            }
          >
            <span
              className={`cursor-pointer text-hover-dark me-4 py-1`}
              onClick={() => {
                stopAudio();
                setIsSpeechPlaying(false);
              }}
            >
              <i className="fa-solid fa-volume-high text-primary fs-5"></i>
            </span>
          </OverlayTrigger>
        ))
      }

      {
        (
          (
            chatMessage.id &&
            chatMessage.generation_type !== ChatMessageGenerationType.MUSIC &&
            chatMessage.generation_type !== ChatMessageGenerationType.TRANSCRIPTION &&
            chatMessage.role == ChatMessageRoleType.USER &&
            currentSettings?.prompt_setting?.music_generation
          ) ||
          (
            isText &&
            isEligibleForTranslationAndTTS &&
            chatMessage.role == ChatMessageRoleType.ASSISTANT &&
            currentSettings?.response_setting?.music_generation
          )
        ) &&
        (
          !isMusicPlaying 
          ?
          (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {intl.formatMessage({
                    id: `chat.message.input.generate.music`,
                  })}
                </Tooltip>
              }
            >
              <span
                className={`${
                  !generatingAudio
                    ? "cursor-pointer text-hover-dark"
                    : generatingMusic && "blinking"
                } me-4 py-1`}
                onClick={() => !generatingAudio && generateAudio("music")}
              >
                <i className={`fa-solid fa-music fs-5`}></i>
              </span>
            </OverlayTrigger>
          )
          : 
          (
            <OverlayTrigger
              placement="top"
              trigger={["hover", "focus"]}
              overlay={
                <Tooltip>
                  {intl.formatMessage({ id: `chat.message.listening` })}
                </Tooltip>
              }
            >
              <span
                className={`cursor-pointer text-hover-dark me-4 py-1`}
                onClick={() => {
                  stopAudio();
                  setIsMusicPlaying(false);
                }}
              >
                <i className="fa-solid fa-music text-primary fs-5"></i>
              </span>
            </OverlayTrigger>
          )
        )
      }

      {
        (
          (
            chatMessage.id &&
            ![ChatMessageGenerationType.IMAGE, ChatMessageGenerationType.IMAGE_UNDERSTANDING, ChatMessageGenerationType.VIDEO_UNDERSTANDING, ChatMessageGenerationType.TRANSCRIPTION].includes(chatMessage.generation_type) &&
            chatMessage.role == ChatMessageRoleType.USER &&
            currentSettings?.prompt_setting?.image_generation
          ) ||
          (
            isText &&
            chatMessage.role == ChatMessageRoleType.ASSISTANT &&
            currentSettings?.response_setting?.image_generation
          )
        ) && 
        (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {intl.formatMessage({
                  id: `chat.message.image_${
                    generatingImage ? "generating" : "generation"
                  }`,
                })}
              </Tooltip>
            }
          >
            <span
              className={`${
                !generatingImage
                  ? "cursor-pointer text-hover-dark"
                  : "blinking"
              } me-4 py-1`}
              onClick={() => !generatingImage && generateImage()}
            >
              <i className="fa-solid fa-image fs-5"></i>
            </span>
          </OverlayTrigger>
        )
      }

      {
        (
          (
            chatMessage.id &&
            chatMessage.role == ChatMessageRoleType.USER &&
            ChatMessageGenerationType.TRANSCRIPTION !== chatMessage.generation_type &&
            currentSettings?.prompt_setting?.image_search
          ) ||
          (
            isText &&
            chatMessage.role == ChatMessageRoleType.ASSISTANT &&
            currentSettings?.response_setting?.image_search
          )
        ) && 
        (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {intl.formatMessage({
                  id: `chat.message.image_${
                    searchingImages ? "searching" : "search"
                  }`,
                })}
              </Tooltip>
            }
          >
            <span
              className={`${
                !searchingImages
                  ? "cursor-pointer text-hover-dark"
                  : "blinking"
              } me-4 py-1 position-relative`}
              onClick={() => !searchingImages && searchImages()}
            >
              <i
                className="fa-solid fa-image position-absolute bottom-50 end-50"
                style={{
                  left: selectedLang === "en" ? "0.28rem" : "auto",
                  right: selectedLang === "en" ? "auto" : "0.49rem",
                  top: isSystemMessage ? "0.45rem" : "0.66rem",
                  fontSize: "0.45rem",
                }}
              ></i>
              <i className="fa-solid fa-magnifying-glass fs-4"></i>
            </span>
          </OverlayTrigger>
        )
      }
        
      {
        (
          (
            chatMessage.id &&
            chatMessage.role == ChatMessageRoleType.USER &&
            ChatMessageGenerationType.TRANSCRIPTION !== chatMessage.generation_type &&
            currentSettings?.prompt_setting?.translate
          ) ||
          (
            isText &&
            isEligibleForTranslationAndTTS &&
            chatMessage.role == ChatMessageRoleType.ASSISTANT &&
            currentSettings?.response_setting?.translate
          )
        ) &&
        !translatedMessage && 
        (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {intl.formatMessage({
                  id: `chat.message.${
                    translatingMessage ? "translating" : "translate"
                  }`,
                })}
              </Tooltip>
            }
          >
            <span
              className={`${
                !translatingMessage
                  ? "cursor-pointer text-hover-dark"
                  : "blinking"
              } me-4 py-1`}
              onClick={() => !translatingMessage && translate()}
            >
              <i className="fa-solid fa-globe fs-5"></i>
            </span>
          </OverlayTrigger>
        )
      }

      {
        (
          chatMessage.role == ChatMessageRoleType.USER ||
          (
            chatMessage.role == ChatMessageRoleType.ASSISTANT &&
            isText
          )
        ) &&
          chatMessage.message_language == "ar" &&
          currentSettings?.response_setting?.diacritize && 
          (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {intl.formatMessage({
                    id: `chat.message.${
                      diacritizingMessage ? "diacritizing" : "diacritize"
                    }`,
                  })}
                </Tooltip>
              }
            >
              <span
                className={`${
                  !diacritizingMessage
                    ? "cursor-pointer text-hover-dark"
                    : "blinking"
                } me-4 py-1`}
                onClick={() => !diacritizingMessage && diacritize()}
              >
                <i className="fa-solid fa-check fs-5"></i>
              </span>
            </OverlayTrigger>
          )
        }

        {
          isLast &&
          chatMessage.role == ChatMessageRoleType.ASSISTANT &&
          currentSettings?.response_setting?.regenerate &&
          (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {intl.formatMessage({
                    id: "chat.message.regenerate.title",
                  })}
                </Tooltip>
              }
            >
              <span
                className="cursor-pointer text-hover-dark regenerate-btn py-1"
                onClick={handleRegenerate}
              >
                <i className="fa-solid fa-rotate fs-5"></i>
              </span>
            </OverlayTrigger>
          )
        }
        
    </div>
}

export { ChatMessageOptions }